/**
 * Feature flag keys — string constants for every gated feature in the app.
 *
 * Keys match the keys stored in the `app_config/tier_definitions` Firestore document.
 *
 * Usage:
 * ```ts
 * const { hasFeature } = useFeatureFlag(FeatureKeys.exportTripPdf);
 * ```
 */
export const FeatureKeys = {
    // ── Authentication & User Management ──────────────────────────────────
    login: 'login',
    logout: 'logout',
    editProfile: 'edit_profile',
    viewProfile: 'view_profile',
    openDrawer: 'open_drawer',

    // ── Trip Management ────────────────────────────────────────────────────
    viewTripList: 'view_trip_list',
    createTripManual: 'create_trip_manual',
    createTripAi: 'create_trip_ai',
    editTrip: 'edit_trip',
    deleteTrip: 'delete_trip',
    viewTripDetail: 'view_trip_detail',
    viewTripTimeline: 'view_trip_timeline',

    // ── Trip Content ───────────────────────────────────────────────────────
    addActivity: 'add_activity',
    editActivity: 'edit_activity',
    deleteActivity: 'delete_activity',
    addAccommodation: 'add_accommodation',
    editAccommodation: 'edit_accommodation',
    deleteAccommodation: 'delete_accommodation',
    addFlight: 'add_flight',
    editFlight: 'edit_flight',
    deleteFlight: 'delete_flight',
    addCarRental: 'add_car_rental',
    editCarRental: 'edit_car_rental',
    deleteCarRental: 'delete_car_rental',
    addRegion: 'add_region',
    editRegion: 'edit_region',
    deleteRegion: 'delete_region',
    viewMap: 'view_map',
    interactMap: 'interact_map',

    // ── Expenses & Budget ──────────────────────────────────────────────────
    addExpense: 'add_expense',
    editExpense: 'edit_expense',
    deleteExpense: 'delete_expense',
    viewExpenseAnalytics: 'view_expense_analytics',
    exportExpenses: 'export_expenses',
    filterExpenses: 'filter_expenses',

    // ── Documents & Equipment ──────────────────────────────────────────────
    addTravelDocument: 'add_travel_document',
    editTravelDocument: 'edit_travel_document',
    deleteTravelDocument: 'delete_travel_document',
    addTripDocument: 'add_trip_document',
    editTripDocument: 'edit_trip_document',
    deleteTripDocument: 'delete_trip_document',
    createEquipmentList: 'create_equipment_list',
    editEquipmentList: 'edit_equipment_list',
    deleteEquipmentList: 'delete_equipment_list',
    checkEquipmentItem: 'check_equipment_item',

    // ── Collaboration ──────────────────────────────────────────────────────
    addParticipant: 'add_participant',
    removeParticipant: 'remove_participant',
    changeParticipantRole: 'change_participant_role',
    shareTemplate: 'share_template',
    redeemTemplate: 'redeem_template',

    // ── Data Management ────────────────────────────────────────────────────
    importTripJson: 'import_trip_json',
    exportTripJson: 'export_trip_json',
    selectiveExportJson: 'selective_export_json',
    importIntoTripJson: 'import_into_trip_json',
    // Import a plan from an external file. One key per source so each can be
    // sold/toggled independently.
    importPlanExcel: 'import_plan_excel',
    importPlanDocument: 'import_plan_document',
    importPlanMap: 'import_plan_map',
    exportTripPdf: 'export_trip_pdf',
    exportTripCalendar: 'export_trip_calendar',
    exportTripExcel: 'export_trip_excel',
    exportTripCsv: 'export_trip_csv',
    duplicateTrip: 'duplicate_trip',
    parseEmailBooking: 'parse_email_booking',
    generateBlogPost: 'generate_blog_post',

    // ── System & Admin ─────────────────────────────────────────────────────
    viewNotifications: 'view_notifications',
    sendFeedback: 'send_feedback',
    viewMetricsDashboard: 'view_metrics_dashboard',
    exportMetrics: 'export_metrics',

    // ── API-backed: Gemini AI ──────────────────────────────────────────────
    /** Shared daily counter for all AI queries. */
    geminiAiQuery: 'gemini_ai_query',
    geminiParseEmail: 'gemini_parse_email',
    /** AI assistant for suggesting & enriching activities. Daily counter. */
    aiActivitiesAssistant: 'ai_activities_assistant',
    /** AI assistant for suggesting & enriching accommodations. Daily counter. */
    aiAccommodationsAssistant: 'ai_accommodations_assistant',

    // ── API-backed: Google Places ──────────────────────────────────────────
    /** Shared daily counter for search + detail calls. */
    googlePlacesSearch: 'google_places_search',

    // ── API-backed: AviationStack ─────────────────────────────────────────
    aviationLookup: 'aviation_lookup',
    aviationRouteSearch: 'aviation_route_search',

    // ── API-backed: Gmail ──────────────────────────────────────────────────
    gmailFetchList: 'gmail_fetch_list',
    gmailFetchDetail: 'gmail_fetch_detail',

    // ── Screens & UI Features ─────────────────────────────────────────────
    /** @deprecated Use createTripAi instead. */
    aiTripPlannerScreen: 'ai_trip_planner_screen',
    aiTripSuggestions: 'ai_trip_suggestions',
    /** Daily counter. */
    aiTripImprovement: 'ai_trip_improvement',
    emailBookingsInbox: 'email_bookings_inbox',
    offlineAccess: 'offline_access',
    routeOptimization: 'route_optimization',
    flightAlerts: 'flight_alerts',
    prioritySupport: 'priority_support',
    /** Total-count OCR scans. */
    documentOcr: 'document_ocr',
    entityEnrichment: 'entity_enrichment',
    /** Monthly counter. */
    writeBlogPost: 'write_blog_post',

    // ── Family Tier ────────────────────────────────────────────────────────
    familyMemberSeats: 'family_member_seats',
    sharedTripLibrary: 'shared_trip_library',
    unifiedFamilyBudget: 'unified_family_budget',
} as const;

/** Union type of all feature key values. */
export type FeatureKey = (typeof FeatureKeys)[keyof typeof FeatureKeys];
